import { CORE_COUNTRIES, PLUS_COUNTRIES } from '@marketplace/config/country.js'
import { isInDateRange } from '@marketplace/helpers/datetime.js'
import { trackClick } from '@marketplace/helpers/segment/index.js'
import { hasFeature } from '@marketplace/helpers/url.js'

export const helpers = {
    get isConnectAndGrowActive() {
        return window?.Store?.isConnectAndGrowActive || false
    },

    get isFreeShippingWeekActive() {
        const isRetailer = window.Store?.accountType === 'retailer'
        const userCountry = window.Store?.country
        const isCoreUser = PLUS_COUNTRIES.includes(userCountry)
        const eventDays = isInDateRange('2024-03-04', '2024-03-10')
        return isRetailer && isCoreUser && eventDays
    },

    get isHiddenGemsActive() {
        return true
    },

    get isOrderchampPlusTrialDays() {
        return (
            hasFeature('oc_plus_trial_days_test') ||
            window?.Store?.isOrderchampPlusTrialDays ||
            false
        )
    },

    get isReorderDaysActive() {
        return (
            hasFeature('reorder_test') ||
            window?.Store?.isReorderDaysActive ||
            false
        )
    },

    get isShopLocalDaysActive() {
        return (
            hasFeature('sld_test') ||
            window?.Store?.isShopLocalDaysActive ||
            false
        )
    },

    get isAccountSideBarPromoActive() {
        return isInDateRange('2022-01-24', '2022-02-13')
    },

    // account related helpers: window.Store is created in twig head.html.twig
    get isLoggedIn() {
        return window.Store?.accountId
    },
    get isSupplierAccount() {
        return window.Store?.accountType === 'supplier'
    },
    get isRetailerAccount() {
        return window.Store?.accountType === 'retailer'
    },
    get isInCoreCountry() {
        return CORE_COUNTRIES.includes(window.Store?.country.toUpperCase())
    },

    toRelativePath: (path) => {
        let url
        try {
            url = new URL(path)
        } catch (e) {
            return path
        }
        return url.pathname + url.search
    },

    /**
     * Takes in a vue routename and returns the name that should be used in segment as the category trait.
     *
     * @param {string} routeName
     * @returns {string}
     */
    getSegmentCategoryNamebyRoute(routeName) {
        if (routeName?.includes('storefront')) {
            return 'Storefront'
        }

        if (routeName?.includes('home')) {
            return 'HomePage'
        }

        switch (routeName) {
            case 'marketplace':
                return 'Marketplace'

            case 'listings':
                return 'Marketplace'

            case 'cart':
                return 'Cart'

            case 'product':
                return 'Product Page'

            case 'productsearch':
                return 'Marketplace'

            case 'account-favorites':
                return 'Favorites'

            case 'search':
                return 'Search'

            case 'landing-page':
                return 'Langing Page'

            default:
                return `Route ${routeName} unspecified in getSegmentCategoryNamebyRoute`
        }
    },

    /**
     * When a function has to do something and then window.reload show a spinner that hides the app
     * Used when changing languages or currency, and in some GQL error cases
     *
     * @param {string} redirectUrl
     * @param {number} timeout
     */
    triggerReloadSpinner(redirectUrl = null, timeout = 150) {
        const VueReloadLoader = document.getElementById('js-vue-reload-loader')
        const VueBelowHeader = document.getElementById('js-vue-below-header')

        if (VueReloadLoader) VueReloadLoader.style.display = 'block'
        if (VueBelowHeader) VueBelowHeader.classList.add('opacity-0')

        setTimeout(() => {
            if (redirectUrl) {
                window.location.href = redirectUrl
            } else {
                window.location.reload()
            }
        }, timeout)
    },

    ocFreeShipping: {
        get oc_freeshipping_threshold() {
            return window.Store.oc_freeshipping_thresholds
        },
        get oc_freeshipping_active() {
            return Boolean(window.Store.oc_freeshipping_active)
        },
    },

    urlOrigin: (() => {
        return window.location.origin.replace('www', 'cdn')
    })(), // immediately invoked function execution

    onDuplicateNavigation(failure) {
        if (failure.name === 'NavigationDuplicated') {
            window.location.reload()
        }
    },
    preferredCategoryPath(categoryPath, overwritePath = '', skipCheck = false) {
        const categoryParts = categoryPath?.split('/')?.length || 0

        if (!skipCheck && overwritePath) {
            return `/${overwritePath}/${categoryPath}`
        }

        if (!skipCheck && categoryParts > 1 && this.isLoggedIn) {
            return `/listings/${categoryPath}`
        }

        if (!skipCheck) {
            return `/marketplace/${categoryPath}`
        }

        return `/${categoryPath}`
    },

    // Check for dot in a string
    checkForDot(value) {
        return value.includes('.') // Simplified to return true if dot is found, false otherwise
    },
}

export const segment = {
    // we need to get rid of this TODO
    trackClick,
}

/**
 * Translate property of an object.
 *
 * @param {object} object
 * @param {string} property
 * @param {string} locale
 * @returns {string | null}
 */
export function tp(object, property, locale = null) {
    locale = locale || this.$locale

    if (!object) return null

    if (!object.translation && !object.translations) {
        console.error('Object does not have translation(s) property:', object)

        return object[property]
    }

    return (
        object.translation[property] ||
        object.translations?.[locale]?.[property] ||
        object[property] ||
        ''
    )
}
